import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert
} from '@mui/material';
import { format } from 'date-fns';
import axiosInstance from '../utils/axiosConfig';
import { DarkDate, DarkDateCategory } from '../types/mongodb';

interface DarkDateDialogProps {
  open: boolean;
  date: Date | null;
  onClose: () => void;
  onSuccess: () => void;
}

const CATEGORIES: { value: DarkDateCategory; label: string }[] = [
  { value: 'BANK_HOLIDAY', label: 'Bank Holiday' },
  { value: 'VENUE_CLOSED', label: 'Venue Closed' },
  { value: 'STRATEGIC_GAP', label: 'Strategic Gap' },
  { value: 'OTHER', label: 'Other' }
];

const DarkDateDialog: React.FC<DarkDateDialogProps> = ({ open, date, onClose, onSuccess }) => {
  const [category, setCategory] = useState<DarkDateCategory>('VENUE_CLOSED');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!date || !reason.trim()) {
      setError('Please provide a reason');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await axiosInstance.post('/api/dark-dates', {
        date: date.toISOString(),
        category,
        reason: reason.trim(),
        notes: notes.trim() || undefined
      });

      handleClose();
      onSuccess();
    } catch (err: any) {
      console.error('Failed to create dark date:', err);
      setError(err.response?.data?.error || 'Failed to mark date as dark');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCategory('VENUE_CLOSED');
    setReason('');
    setNotes('');
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Mark Date as Dark
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Date"
            value={date ? format(date, 'EEEE, MMMM do, yyyy') : ''}
            disabled
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={category}
              onChange={(e) => setCategory(e.target.value as DarkDateCategory)}
              label="Category"
            >
              {CATEGORIES.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            placeholder="e.g., Christmas Day, Venue maintenance"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Additional Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={3}
            placeholder="Optional additional information"
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !reason.trim()}
        >
          {loading ? 'Saving...' : 'Mark as Dark'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DarkDateDialog;